import React, { useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

const EnglishLearningChat = () => {
  const [openImage, setOpenImage] = useState(null);

  // Project data
  const project = {
    title: "English Learning Chat Agent",
    overview:
      "I've developed an advanced AI chatbot system for English language education that is currently being used by over 500 public schools, serving thousands of students daily as part of a government project. The system uses a multi-agent architecture to create realistic English conversation scenarios for both general education and vocational training.",
    images: [
      {
        src: "/images/english_learning_chat/teacher_input.jpg",
        title: "Teacher Configuration Interface",
        description: "Teachers can customize scenarios and learning objectives",
      },
      {
        src: "/images/english_learning_chat/select_character.png",
        title: "Character Selection",
        description:
          "Students can choose conversation partners like CoCo, Alice, or Customer",
      },
      {
        src: "/images/english_learning_chat/student_report.png",
        title: "Student Performance Reports",
        description: "Detailed analytics for teachers to track progress",
      },
      {
        src: "/images/english_learning_chat/class_report.png",
        title: "Class Performance Report",
        description: "Aggregated class performance and analytics",
      },
      {
        src: "/images/english_learning_chat/alix_simple_workflow.png",
        title: "System Workflow",
        description:
          "How the different AI agents interact during a conversation",
      },
    ],
    keyFeatures: [
      {
        title: "Multi-Agent System",
        description:
          "Multiple specialized AI agents working together to create a cohesive learning experience",
        subFeatures: [
          "Conversation Bot: Simulates realistic English interactions",
          "Hint Bot: Automatically detects when students need assistance and provides appropriate hints",
          "Conversation Manager: Gracefully ends conversations to maintain realism",
          "Reporting Bot: Generates detailed performance reports for teachers",
        ],
      },
      {
        title: "Teacher Control Panel",
        description:
          "Allows educators to input specific parameters and customize the learning experience while maintaining ease of use",
      },
      {
        title: "Adaptive Difficulty Levels",
        description:
          "Proprietary prompting system adjusts language complexity based on grade level and curriculum standards",
      },
    ],
    technicalImplementation:
      "The system processes teacher inputs to generate appropriate conversation scenarios, which are then assigned to students as homework or classwork. The multi-agent architecture ensures that conversations remain engaging, educational, and tailored to each student's proficiency level.",
    challenges: [
      "Balancing Flexibility and Usability: Created an interface that gives teachers significant customization options while remaining intuitive and straightforward",
      "Language Difficulty Calibration: Developed detailed requirements for different grade levels based on curriculum standards to ensure appropriate challenge levels",
      "Scenario Coverage: Designed the system to handle a wide variety of conversation types and contexts, particularly for vocational training",
    ],
    impact:
      "The system has received overwhelmingly positive feedback from both teachers and students. It successfully bridges the gap between classroom learning and practical English usage, providing students with valuable conversation practice in a controlled, educational environment.",
  };

  // Image modal handlers
  const handleImageClick = (img) => setOpenImage(img);
  const handleClose = () => setOpenImage(null);

  // Component for displaying an image card with description
  const ImageCard = ({ image, title, description }) => (
    <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
      <Box sx={{ cursor: "pointer" }} onClick={() => handleImageClick(image)}>
        <img
          src={image.src}
          alt={image.title}
          style={{
            width: "100%",
            height: 260,
            objectFit: "cover",
            borderRadius: "8px",
          }}
        />
      </Box>
      <Typography variant="h6" sx={{ mt: 2 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {description}
      </Typography>
    </Paper>
  );

  return (
    <>
      {/* Image Lightbox Dialog */}
      <Dialog open={!!openImage} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "#111" }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#fff",
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="large" />
          </IconButton>
          {openImage && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
              }}
            >
              <img
                src={openImage.src}
                alt={openImage.title}
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  width: "auto",
                  height: "auto",
                  borderRadius: 8,
                  boxShadow: "0 4px 32px #000a",
                  background: "#222",
                }}
              />
              <Typography variant="h6" color="#fff" sx={{ mt: 2 }}>
                {openImage.title}
              </Typography>
              <Typography variant="body2" color="#ccc" sx={{ mt: 1, mb: 1 }}>
                {openImage.description}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Overview */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {project.title}
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem" }}>
          {project.overview}
        </Typography>
      </Paper>

      {/* Demo Video Section - Moved to top for better product showcase */}
      <Box
        sx={{
          mb: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Product Demo
        </Typography>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <video
            controls
            style={{
              width: "100%",
              maxWidth: 800,
              borderRadius: 8,
              background: "#000",
              display: "block",
            }}
          >
            <source src="/videos/alix_video_web.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </Box>
      </Box>

      {/* User Experience Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          User Experience
        </Typography>
        <Grid container spacing={3}>
          {/* Teacher Experience */}
          <Grid item xs={12} md={6}>
            <ImageCard
              image={project.images[0]}
              title="Teacher Input Interface"
              description="Our intuitive interface allows teachers to create customized conversation scenarios by setting specific learning objectives, conversation topics, and difficulty levels. The system handles the complexity while keeping the teacher experience straightforward."
            />
          </Grid>
          {/* Student Experience */}
          <Grid item xs={12} md={6}>
            <ImageCard
              image={project.images[1]}
              title="Interactive Character Selection"
              description="Students engage with the system by selecting conversation partners that match their learning scenario. Characters like CoCo, Alice, and Customer provide varied conversation styles and personalities, making the learning experience more engaging and realistic."
            />
          </Grid>
        </Grid>
      </Box>

      {/* Analytics & Reporting Section */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Analytics & Reporting
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ImageCard
              image={project.images[2]}
              title="Individual Performance Tracking"
              description="The system generates comprehensive reports for each student, highlighting strengths, areas for improvement, and specific language skills development. Teachers can review conversation transcripts and see detailed metrics on fluency, grammar, and vocabulary usage."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ImageCard
              image={project.images[3]}
              title="Classroom-Level Analytics"
              description="Teachers can view aggregated data across their entire class, identifying common challenges and measuring overall progress. The system categorizes performance levels and provides actionable insights for curriculum planning and targeted intervention strategies."
            />
          </Grid>
        </Grid>
      </Box>

      {/* Technical Details & Features Section */}
      <Grid container spacing={4} sx={{ mb: 5 }}>
        {/* Key Features */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, height: "100%" }}>
            <Typography variant="h5" gutterBottom>
              Key Features
            </Typography>
            <List>
              {project.keyFeatures.map((feature, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="h6">{feature.title}</Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body1"
                            color="text.primary"
                            sx={{ display: "block", mt: 1 }}
                          >
                            {feature.description}
                          </Typography>
                          {feature.subFeatures && (
                            <List dense sx={{ ml: 2, mt: 1 }}>
                              {feature.subFeatures.map(
                                (subFeature, subIndex) => (
                                  <ListItem key={subIndex} sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CheckCircleIcon
                                        fontSize="small"
                                        color="success"
                                      />
                                    </ListItemIcon>
                                    <ListItemText primary={subFeature} />
                                  </ListItem>
                                )
                              )}
                            </List>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  {index < project.keyFeatures.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Technical Implementation */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 4, height: "100%" }}>
            <Typography variant="h5" gutterBottom>
              Technical Implementation
            </Typography>
            <Typography variant="body1" paragraph>
              {project.technicalImplementation}
            </Typography>
            <Box sx={{ mb: 3, mt: 2 }}>
              <Box
                sx={{ cursor: "pointer" }}
                onClick={() => handleImageClick(project.images[4])}
              >
                <img
                  src={project.images[4].src}
                  alt={project.images[4].title}
                  style={{
                    width: "100%",
                    height: 260,
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The workflow diagram illustrates how user messages are processed
                through security checks, analyzed by the hint judge system, and
                routed to appropriate response bots before final evaluation and
                reporting.
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Results & Challenges Section */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Grid container spacing={4}>
          {/* Challenges Overcome */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              Challenges Overcome
            </Typography>
            <List>
              {project.challenges.map((challenge, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={challenge}
                    primaryTypographyProps={{ variant: "body1" }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>

          {/* Impact and Results */}
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              Impact and Results
            </Typography>
            <Typography variant="body1" paragraph>
              {project.impact}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Current Deployment
              </Typography>
              <Typography variant="body1">• 500+ public schools</Typography>
              <Typography variant="body1">
                • Thousands of daily active students
              </Typography>
              <Typography variant="body1">
                • Government-sponsored education initiative
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default EnglishLearningChat;
